import { SlidersHorizontal, Search, Calendar, ChevronDown, ArrowRight } from 'lucide-react';
import MultiSelect from './MultiSelect';
import { useState } from 'react';

interface ValidationFiltersProps {
  searchTerm: string;
  selectedStatus: string[];
  selectedCompany: string[];
  selectedAgency: string[];
  selectedNegotiator: string[];
  showRegulOnly: boolean;
  showClosedAndCancelled: boolean;
  encaissementDateRange: { startDate: string | null; endDate: string | null };
  appliedEncaissementDateRange: { startDate: string | null; endDate: string | null };
  compromiseDateRange: { startDate: string | null; endDate: string | null };
  appliedCompromiseDateRange: { startDate: string | null; endDate: string | null };
  applyEncaissementDateFilter: () => void;
  clearEncaissementDateFilter: () => void;
  applyCompromiseDateFilter: () => void;
  clearCompromiseDateFilter: () => void;
  agencies: { name: string; company: string }[];
  negotiators: string[];
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string[]) => void;
  onCompanyChange: (value: string[]) => void;
  onAgencyChange: (value: string[]) => void;
  onNegotiatorChange: (value: string[]) => void;
  onRegulOnlyChange: (value: boolean) => void;
  onShowClosedAndCancelledChange: (value: boolean) => void;
  onEncaissementDateRangeChange: (range: { startDate: string | null; endDate: string | null }) => void;
  onCompromiseDateRangeChange: (range: { startDate: string | null; endDate: string | null }) => void;
}

const STATUS_OPTIONS = [
  { value: 'potentiel', label: 'Potentiel' },
  { value: 'compromis', label: 'Compromis' },
  { value: 'acte', label: 'Acté' },
  { value: 'encaisse', label: 'Encaissé' },
  { value: 'cloture', label: 'Clôturé' },
  { value: 'annule', label: 'Annulé' }
];

const COMPANY_OPTIONS = [
  { value: 'KI', label: 'Kermarrec Immobilier' },
  { value: 'KA', label: 'Kermarrec Atlantique' },
  { value: 'DRYADE', label: 'Dryade' }
];

const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors bg-white';
const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5';

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors cursor-pointer focus:outline-none ${checked ? 'bg-indigo-600' : 'bg-slate-200'}`}
      aria-pressed={checked}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

export default function ValidationFilters({
  searchTerm,
  selectedStatus,
  selectedCompany,
  selectedAgency,
  selectedNegotiator,
  showRegulOnly,
  showClosedAndCancelled,
  encaissementDateRange,
  appliedEncaissementDateRange,
  compromiseDateRange,
  appliedCompromiseDateRange,
  applyEncaissementDateFilter,
  clearEncaissementDateFilter,
  applyCompromiseDateFilter,
  clearCompromiseDateFilter,
  agencies,
  negotiators,
  onSearchChange,
  onStatusChange,
  onCompanyChange,
  onAgencyChange,
  onNegotiatorChange,
  onRegulOnlyChange,
  onShowClosedAndCancelledChange,
  onEncaissementDateRangeChange,
  onCompromiseDateRangeChange
}: ValidationFiltersProps) {
  const [showEncaissementFilter, setShowEncaissementFilter] = useState(false);
  const [showCompromiseFilter, setShowCompromiseFilter] = useState(false);

  const filteredAgencies = selectedCompany.length > 0
    ? agencies.filter(agency => selectedCompany.includes(agency.company))
    : agencies;

  const agencyOptions = filteredAgencies.map(agency => ({ value: agency.name, label: agency.name }));
  const negotiatorOptions = negotiators.map(negotiator => ({ value: negotiator, label: negotiator }));

  const activeFiltersCount = [
    selectedStatus.length > 0,
    selectedCompany.length > 0,
    selectedAgency.length > 0,
    selectedNegotiator.length > 0,
    appliedEncaissementDateRange.startDate || appliedEncaissementDateRange.endDate,
    appliedCompromiseDateRange.startDate || appliedCompromiseDateRange.endDate,
    showRegulOnly,
  ].filter(Boolean).length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
          <SlidersHorizontal size={14} className="text-indigo-600" />
        </div>
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">Filtres</h2>
        {activeFiltersCount > 0 && (
          <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-bold">
            {activeFiltersCount}
          </span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Search */}
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher par n° de bien, vendeur, acquéreur…"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
          />
        </div>

        {/* 4-col filter grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Statut</label>
            <MultiSelect options={STATUS_OPTIONS} value={selectedStatus} onChange={onStatusChange} placeholder="Tous les statuts" />
          </div>
          <div>
            <label className={labelCls}>Société</label>
            <MultiSelect options={COMPANY_OPTIONS} value={selectedCompany} onChange={onCompanyChange} placeholder="Toutes les sociétés" />
          </div>
          <div>
            <label className={labelCls}>Agence</label>
            <MultiSelect options={agencyOptions} value={selectedAgency} onChange={onAgencyChange} placeholder="Toutes les agences" />
          </div>
          <div>
            <label className={labelCls}>Négociateur</label>
            <MultiSelect options={negotiatorOptions} value={selectedNegotiator} onChange={onNegotiatorChange} placeholder="Tous les négociateurs" />
          </div>
        </div>

        {/* Date filters */}
        <div className="space-y-3">
          {/* Encaissement date */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowEncaissementFilter(!showEncaissementFilter)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Date d'encaissement</span>
                {(appliedEncaissementDateRange.startDate || appliedEncaissementDateRange.endDate) && (
                  <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                    <span>{appliedEncaissementDateRange.startDate || '…'}</span>
                    <ArrowRight size={10} />
                    <span>{appliedEncaissementDateRange.endDate || '…'}</span>
                  </span>
                )}
              </div>
              <ChevronDown size={15} className={`text-slate-400 transition-transform ${showEncaissementFilter ? 'rotate-180' : ''}`} />
            </button>
            {showEncaissementFilter && (
              <div className="p-4 border-t border-slate-100 grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Date début</label>
                  <input type="date" value={encaissementDateRange.startDate || ''} onChange={(e) => onEncaissementDateRangeChange({ ...encaissementDateRange, startDate: e.target.value || null })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Date fin</label>
                  <input type="date" value={encaissementDateRange.endDate || ''} onChange={(e) => onEncaissementDateRangeChange({ ...encaissementDateRange, endDate: e.target.value || null })} className={inputCls} />
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={clearEncaissementDateFilter} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">Effacer</button>
                  <button type="button" onClick={applyEncaissementDateFilter} className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors">Appliquer</button>
                </div>
              </div>
            )}
          </div>

          {/* Compromise date */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowCompromiseFilter(!showCompromiseFilter)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Date de compromis</span>
                {(appliedCompromiseDateRange.startDate || appliedCompromiseDateRange.endDate) && (
                  <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                    <span>{appliedCompromiseDateRange.startDate || '…'}</span>
                    <ArrowRight size={10} />
                    <span>{appliedCompromiseDateRange.endDate || '…'}</span>
                  </span>
                )}
              </div>
              <ChevronDown size={15} className={`text-slate-400 transition-transform ${showCompromiseFilter ? 'rotate-180' : ''}`} />
            </button>
            {showCompromiseFilter && (
              <div className="p-4 border-t border-slate-100 grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Date début</label>
                  <input type="date" value={compromiseDateRange.startDate || ''} onChange={(e) => onCompromiseDateRangeChange({ ...compromiseDateRange, startDate: e.target.value || null })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Date fin</label>
                  <input type="date" value={compromiseDateRange.endDate || ''} onChange={(e) => onCompromiseDateRangeChange({ ...compromiseDateRange, endDate: e.target.value || null })} className={inputCls} />
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={clearCompromiseDateFilter} className="px-4 py-2 text-sm font-medium text-slate600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">Effacer</button>
                  <button type="button" onClick={applyCompromiseDateFilter} className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors">Appliquer</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Toggle options */}
        <div className="flex flex-col sm:flex-row gap-4 pt-1">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <Toggle checked={showRegulOnly} onChange={onRegulOnlyChange} />
            <span className="text-sm font-medium text-slate-700">Régularisations uniquement</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <Toggle checked={showClosedAndCancelled} onChange={onShowClosedAndCancelledChange} />
            <span className="text-sm font-medium text-slate-700">Afficher clôturées et annulées</span>
          </label>
        </div>
      </div>
    </div>
  );
}
